import React, { useState, useEffect } from 'react';
import { ICondition, IAction, IPolicyDefaults, ConjuctionType, Conjuction, RuleElements, IAdditionalRule } from '@app/models';
import { Condition, Action, ElseAction, Operator } from '../components';
import { IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { removeRule, addCondition, addAction, setRuleOperator } from '@app/store/actions';

interface IProps {
    rule: IAdditionalRule;
    defaults: IPolicyDefaults;
    handleValidation: Function;
    isEdit: boolean;
    rulesLoaded?: boolean;
    additionalRulesCount: number;
}

interface RuleValidation {
    condition: boolean;
    action: boolean;
}

export const Rule = (props: IProps) => {
    const { rule, defaults, handleValidation, isEdit, rulesLoaded, additionalRulesCount } = props;

    const [id, setId] = useState<string>('000');
    const [operator, setOperator] = useState<ConjuctionType>(rule.operator ?? Conjuction.and);
    const [allowRemoving, setAllowRemoving] = useState<boolean>(false);
    const [ruleValidation, setRuleValidation] = useState<RuleValidation>({ condition: false, action: false });

    const dispatch = useDispatch();

    const deleteRule = () => {
        if (rule.id) dispatch(removeRule(rule.id));
        handleValidation(true, rule.id, true);
    };

    const setOperatorCallback = (op: ConjuctionType) => {
        setOperator(op);
    };

    const validationCallback = (valid: boolean, type: RuleElements) => {
        switch (type) {
            case RuleElements.condition:
                setRuleValidation((prevState) => ({
                    ...prevState,
                    condition: valid
                }));
                break;
            case RuleElements.action:
                setRuleValidation((prevState) => ({
                    ...prevState,
                    action: valid
                }));
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        additionalRulesCount ? setAllowRemoving(true) : setAllowRemoving(false);
    }, [additionalRulesCount]);

    useEffect(() => {
        if (rule.id) setId(rule.id);
    }, [rule]);

    useEffect(() => {
        if (rule.id !== undefined) dispatch(setRuleOperator(operator, rule.id));
    }, [operator]);

    useEffect(() => {
        switch (isEdit) {
            case false:
                if (rule.conditions.length === 0 && rule.id !== undefined) dispatch(addCondition(null, rule.id));
                break;
            case true:
                if (rulesLoaded && rule.conditions.length === 0 && rule.id !== undefined)
                    dispatch(addCondition(null, rule.id));
                break;
        }
    }, [rule.conditions, rule.id]);

    useEffect(() => {
        switch (isEdit) {
            case false:
                if (rule.actions.length === 0 && rule.id !== undefined) dispatch(addAction(null, rule.id));
                break;
            case true:
                if (rulesLoaded && rule.actions.length === 0 && rule.id !== undefined) dispatch(addAction(null, rule.id));
                break;
        }
    }, [rule.actions, rule.id]);

    useEffect(() => {
        ruleValidation.condition && ruleValidation.action ? handleValidation(true, rule.id) : handleValidation(false, rule.id);
    }, [ruleValidation]);

    return (
        <div className='rule'>
            <div className='rule__header'>
                <div className='rule__aside rule__aside--left'>
                    <Operator text='else if' />
                </div>
                <div className='rule__aside rule__aside--right'>
                    {allowRemoving && (
                        <IconButton size='small' onClick={deleteRule} id='removeRule'>
                            <Close fontSize='inherit' />
                        </IconButton>
                    )}
                </div>
            </div>
            <div className='rule__body'>
                {rule.conditions?.length && rule.conditions.map((condition: ICondition, index: number) => (
                    <Condition
                        key={condition.id}
                        index={index}
                        ruleID={id}
                        condition={condition}
                        isLast={rule.conditions?.length === index + 1}
                        operator={operator}
                        conditionOperator={setOperatorCallback}
                        validation={validationCallback}
                        patchData={isEdit ? condition.object : null}
                    />
                ))}

                {rule.actions.length && rule.actions.map((action: IAction, index: number) => (
                    <Action
                        key={action.id}
                        index={index}
                        ruleID={id}
                        action={action}
                        isLast={rule.actions.length === index + 1}
                        defaults={defaults}
                        validation={validationCallback}
                        patchData={isEdit ? action.object : null}
                    />
                ))}

                {rule?.elseActions?.length ? (
                    rule?.elseActions.map((elseAction: IAction, index: number) => (
                        <ElseAction
                            key={elseAction.id}
                            index={index}
                            elseAction={elseAction}
                            isLast={rule.elseActions?.length === index + 1}
                            defaults={defaults}
                            patchData={isEdit ? elseAction.object : null}
                        />
                    ))
                ) : null}
            </div>
        </div>
    );
};
